import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, UserPlus, ArrowLeft, Phone, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { usuarioService } from '../services/api';

const RegistroPage = () => {
    const [nome, setNome] = useState('');
    const [email, setEmail] = useState('');
    const [telefone, setTelefone] = useState('');
    const [senha, setSenha] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // -------------------------------------------------------------
    // VALIDAÇÃO DE SENHA FORTE (MANTIDA INTEGRALMENTE)
    // -------------------------------------------------------------
    const reqLength = senha.length >= 8;
    const reqLetters = /[A-Z]/.test(senha) && /[a-z]/.test(senha);
    const reqNumber = /[0-9]/.test(senha);
    const reqSpecial = /[^A-Za-z0-9]/.test(senha);

    const senhaValida = reqLength && reqLetters && reqNumber && reqSpecial;

    // Calcula a pontuação para a barra de força (0 a 4)
    const forcaSenha = [reqLength, reqLetters, reqNumber, reqSpecial].filter(Boolean).length;
    
    const getCorBarraForca = () => {
        if (forcaSenha === 0) return 'bg-slate-200';
        if (forcaSenha === 1) return 'bg-red-500';
        if (forcaSenha === 2) return 'bg-amber-500';
        if (forcaSenha === 3) return 'bg-blue-500';
        return 'bg-[#6D28D9]'; // Alterado para o Roxo da nova identidade
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        
        if (!senhaValida) {
            alert("Por favor, crie uma senha mais forte atendendo a todos os requisitos.");
            return;
        }

        setLoading(true);

        const dadosParaEnvio = { nome, email, senha, telefone };

        try {
            const response = await usuarioService.registrar(dadosParaEnvio);
            if (response.status === 201 || response.status === 200) {
                alert("Conta criada com sucesso! Verifique seu e-mail.");
                navigate('/login');
            }
        } catch (error) {
            const mensagemErro = error.response?.data?.erro || "Erro ao conectar com o servidor.";
            alert(mensagemErro);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative min-h-screen flex items-center justify-center bg-[#F8F7FF] font-sans p-6 overflow-hidden">
            
            {/* Efeitos de fundo harmonizados */}
            <div className="absolute inset-0 opacity-[0.05] pointer-events-none bg-[radial-gradient(#6D28D9_1px,transparent_1px)] bg-[size:40px_40px]"></div>
            <div className="absolute w-[500px] h-[500px] bg-gradient-to-r from-blue-600/10 to-purple-600/10 blur-[120px] rounded-full pointer-events-none -top-[10%] -right-[5%]"></div>
            <div className="absolute w-[500px] h-[500px] bg-gradient-to-r from-blue-600/10 to-purple-600/10 blur-[120px] rounded-full pointer-events-none -bottom-[10%] -left-[5%]"></div>

            {/* Link de Voltar */}
            <Link to="/" className="absolute top-8 left-8 flex items-center gap-2 no-underline text-[#94a3b8] text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-300 hover:text-[#6D28D9] hover:-translate-x-1">
                <ArrowLeft size={14} /> Voltar ao Início
            </Link>

            {/* Card de Registro */}
            <div className="relative z-10 w-full max-w-[460px] bg-white rounded-[40px] p-8 sm:p-12 border border-purple-50 shadow-[0_20px_50px_rgba(109,40,217,0.05)] animate-fade-in-up mt-8 mb-8">
                
                <div className="text-center">
                    {/* Badge Comece Grátis */}
                    <div className="inline-flex items-center gap-2 bg-[#F5F3FF] px-3 py-1 rounded-full text-[#6D28D9] text-[9px] font-black uppercase tracking-[0.1em] mb-6">
                        <span className="w-1.5 h-1.5 bg-[#6D28D9] rounded-full"></span>
                        Comece grátis agora
                    </div>
                    
                    {/* Logo Centralizada */}
                    <div className="flex items-center justify-center mb-5">
                        <img 
                            src="/logo-sincro2.png" 
                            alt="Agenda Sincro Logo" 
                            className="h-[70px] w-auto object-contain drop-shadow-[0_4px_6px_rgba(0,0,0,0.05)]"
                        />
                    </div>
                    
                    <p className="text-[#94a3b8] text-xs font-bold uppercase tracking-[0.1em] mb-8">
                        Automatize seus atendimentos
                    </p>
                </div>

                {/* Formulário */}
                <form onSubmit={handleRegister}>
                    
                    {/* Nome Completo */}
                    <div className="mb-5 text-left">
                        <label className="block text-[10px] font-black text-[#94a3b8] uppercase tracking-[0.15em] mb-2 ml-1">Nome Completo</label>
                        <div className="relative flex items-center group">
                            <User size={18} className="absolute left-4 text-[#94a3b8] transition-colors duration-300 group-focus-within:text-[#6D28D9]" />
                            <input 
                                type="text" 
                                className="w-full bg-[#F8F7FF] border border-slate-100 py-3.5 pl-12 pr-4 rounded-[18px] text-sm font-medium text-[#1e293b] outline-none transition-all duration-300 focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-purple-600/5"
                                placeholder="Como quer ser chamado?"
                                value={nome}
                                onChange={(e) => setNome(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    {/* E-mail */}
                    <div className="mb-5 text-left">
                        <label className="block text-[10px] font-black text-[#94a3b8] uppercase tracking-[0.15em] mb-2 ml-1">E-mail Profissional</label>
                        <div className="relative flex items-center group">
                            <Mail size={18} className="absolute left-4 text-[#94a3b8] transition-colors duration-300 group-focus-within:text-[#6D28D9]" />
                            <input 
                                type="email" 
                                className="w-full bg-[#F8F7FF] border border-slate-100 py-3.5 pl-12 pr-4 rounded-[18px] text-sm font-medium text-[#1e293b] outline-none transition-all duration-300 focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-purple-600/5"
                                placeholder="seu@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    {/* WhatsApp / Telefone */}
                    <div className="mb-5 text-left">
                        <label className="block text-[10px] font-black text-[#94a3b8] uppercase tracking-[0.15em] mb-2 ml-1">WhatsApp / Telefone</label>
                        <div className="relative flex items-center group">
                            <Phone size={18} className="absolute left-4 text-[#94a3b8] transition-colors duration-300 group-focus-within:text-[#6D28D9]" />
                            <input 
                                type="text" 
                                className="w-full bg-[#F8F7FF] border border-slate-100 py-3.5 pl-12 pr-4 rounded-[18px] text-sm font-medium text-[#1e293b] outline-none transition-all duration-300 focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-purple-600/5"
                                placeholder="(00) 00000-0000"
                                value={telefone}
                                onChange={(e) => setTelefone(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    {/* Senha */}
                    <div className="mb-5 text-left">
                        <label className="block text-[10px] font-black text-[#94a3b8] uppercase tracking-[0.15em] mb-2 ml-1">Senha de Acesso</label>
                        <div className="relative flex items-center group">
                            <Lock size={18} className="absolute left-4 text-[#94a3b8] transition-colors duration-300 group-focus-within:text-[#6D28D9]" />
                            <input 
                                type="password" 
                                className="w-full bg-[#F8F7FF] border border-slate-100 py-3.5 pl-12 pr-4 rounded-[18px] text-sm font-medium text-[#1e293b] outline-none transition-all duration-300 focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-purple-600/5"
                                placeholder="Crie uma senha forte"
                                value={senha}
                                onChange={(e) => setSenha(e.target.value)}
                                required
                            />
                        </div>

                        {/* Indicador de Força da Senha */}
                        {senha.length > 0 && (
                            <div className="mt-4 p-4 bg-[#F8F7FF] rounded-2xl border border-purple-50 animate-fade-in-up">
                                <div className="flex gap-1 mb-3 h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                                    <div className={`h-full transition-all duration-500 ${getCorBarraForca()}`} style={{ width: `${(forcaSenha / 4) * 100}%` }}></div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <span className={`text-[11px] font-bold flex items-center gap-2 ${reqLength ? 'text-[#6D28D9]' : 'text-slate-400'}`}>
                                        <CheckCircle2 size={14} className={reqLength ? 'text-[#6D28D9]' : 'text-slate-300'} /> 
                                        Mínimo de 8 caracteres
                                    </span>
                                    <span className={`text-[11px] font-bold flex items-center gap-2 ${reqLetters ? 'text-[#6D28D9]' : 'text-slate-400'}`}>
                                        <CheckCircle2 size={14} className={reqLetters ? 'text-[#6D28D9]' : 'text-slate-300'} /> 
                                        Letras maiúsculas e minúsculas
                                    </span>
                                    <span className={`text-[11px] font-bold flex items-center gap-2 ${reqNumber ? 'text-[#6D28D9]' : 'text-slate-400'}`}>
                                        <CheckCircle2 size={14} className={reqNumber ? 'text-[#6D28D9]' : 'text-slate-300'} /> 
                                        Pelo menos 1 número
                                    </span>
                                    <span className={`text-[11px] font-bold flex items-center gap-2 ${reqSpecial ? 'text-[#6D28D9]' : 'text-slate-400'}`}>
                                        <CheckCircle2 size={14} className={reqSpecial ? 'text-[#6D28D9]' : 'text-slate-300'} /> 
                                        Caractere especial (Ex: @, #, !)
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Botão de Envio */}
                    <button 
                        type="submit" 
                        className="w-full py-4 mt-2 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none rounded-[18px] text-[11px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-3 shadow-[0_10px_25px_rgba(109,40,217,0.25)] transition-all duration-300 hover:opacity-90 active:scale-[0.98] disabled:opacity-50" 
                        disabled={loading || (!senhaValida && senha.length > 0)}
                    >
                        {loading ? "Processando..." : (
                            <>
                                Criar Minha Conta <UserPlus size={18} />
                            </>
                        )}
                    </button>
                </form>

                {/* Footer do Card */}
                <div className="mt-8 pt-6 border-t border-slate-50 text-xs font-bold text-[#94a3b8] uppercase tracking-[0.05em] text-center">
                    Já possui uma conta? 
                    <Link to="/login" className="text-[#6D28D9] no-underline ml-1 hover:underline">Fazer Login</Link>
                </div>

                {/* Emblema de segurança */}
                <div className="mt-8 flex items-center justify-center gap-2 text-[#94a3b8] opacity-50 text-[9px] font-black uppercase tracking-[0.1em]">
                    <ShieldCheck size={14} /> Dados protegidos por criptografia
                </div>
            </div>
        </div>
    );
};

export default RegistroPage;